import { useEffect, useState } from "react";
import {
  Activity,
  Briefcase,
  Building2,
  Eye,
  Inbox,
  LayoutDashboard,
  Mail,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDict } from "@/lib/i18n/context";
import { cn } from "@/lib/utils";
import type { ContactMessage } from "@/lib/types";

type Tab =
  | "overview"
  | "support"
  | "activity"
  | "people"
  | "companies"
  | "jobs";

type Overview = {
  managers: number;
  candidates: number;
  companies: number;
  jobs: number;
  interviews: number;
  interviews_by_status: Record<string, number>;
  contact_unread: number;
  page_views_total: number;
  unique_visitors: number;
  page_views_7d: number;
  page_views_30d: number;
  unique_visitors_30d: number;
  views_by_day: { day: string; count: number }[];
  top_paths: { path: string; count: number }[];
};

type ActivityItem = {
  id: string;
  kind: string;
  summary: string;
  created_at: string;
};

type AdminUser = {
  id: string;
  email: string;
  full_name: string | null;
  country: string | null;
  role: string;
  company_name: string | null;
  created_at: string;
  banned_at: string | null;
};

type AdminCompany = {
  id: string;
  name: string;
  company_type: string;
  created_at: string;
  user_count: number;
  job_count: number;
};

type AdminJob = {
  id: string;
  title: string;
  company_name: string;
  status: string;
  visibility: string;
  created_at: string;
};

function formatWhen(iso: string, locale: string) {
  try {
    return new Date(iso).toLocaleString(locale === "ar" ? "ar" : "en", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card px-4 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted">
        {label}
      </p>
      <p className="mt-2 text-2xl font-bold tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </div>
  );
}

export function ReportsPage({ currentUserId }: { currentUserId: string }) {
  const t = useDict();
  const [tab, setTab] = useState<Tab>("overview");
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [activity, setActivity] = useState<ActivityItem[] | null>(null);
  const [people, setPeople] = useState<AdminUser[] | null>(null);
  const [companies, setCompanies] = useState<AdminCompany[] | null>(null);
  const [jobs, setJobs] = useState<AdminJob[] | null>(null);

  const [items, setItems] = useState<ContactMessage[] | null>(null);
  const [unread, setUnread] = useState(0);
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [loading, setLoading] = useState(true);

  const tabs: { id: Tab; label: string; icon: typeof LayoutDashboard }[] = [
    { id: "overview", label: t.reports.tabOverview, icon: LayoutDashboard },
    { id: "support", label: t.reports.tabSupport, icon: Inbox },
    { id: "activity", label: t.reports.tabActivity, icon: Activity },
    { id: "people", label: t.reports.tabPeople, icon: Users },
    { id: "companies", label: t.reports.tabCompanies, icon: Building2 },
    { id: "jobs", label: t.reports.tabJobs, icon: Briefcase },
  ];

  useEffect(() => {
    setError(null);
    setActionError(null);
    setLoading(true);

    if (tab === "overview") {
      fetch("/api/admin/overview", { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) throw new Error("fail");
          return res.json() as Promise<{ overview: Overview }>;
        })
        .then((data) => setOverview(data.overview))
        .catch(() => setError(t.reports.loadError))
        .finally(() => setLoading(false));
      return;
    }

    if (tab === "activity") {
      fetch("/api/admin/activity", { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) throw new Error("fail");
          return res.json() as Promise<{ activity: ActivityItem[] }>;
        })
        .then((data) => setActivity(data.activity))
        .catch(() => setError(t.reports.loadError))
        .finally(() => setLoading(false));
      return;
    }

    if (tab === "people") {
      fetch("/api/admin/users", { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) throw new Error("fail");
          return res.json() as Promise<{ users: AdminUser[] }>;
        })
        .then((data) => setPeople(data.users))
        .catch(() => setError(t.reports.loadError))
        .finally(() => setLoading(false));
      return;
    }

    if (tab === "companies") {
      fetch("/api/admin/companies", { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) throw new Error("fail");
          return res.json() as Promise<{ companies: AdminCompany[] }>;
        })
        .then((data) => setCompanies(data.companies))
        .catch(() => setError(t.reports.loadError))
        .finally(() => setLoading(false));
      return;
    }

    if (tab === "jobs") {
      fetch("/api/admin/jobs", { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) throw new Error("fail");
          return res.json() as Promise<{ jobs: AdminJob[] }>;
        })
        .then((data) => setJobs(data.jobs))
        .catch(() => setError(t.reports.loadError))
        .finally(() => setLoading(false));
      return;
    }

    // support
    fetch("/api/contact", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed");
        return res.json() as Promise<{
          items: ContactMessage[];
          unread: number;
        }>;
      })
      .then((data) => {
        setItems(data.items);
        setUnread(data.unread);
      })
      .catch(() => setError(t.reports.loadError))
      .finally(() => setLoading(false));
  }, [tab, t.reports.loadError]);

  async function refreshPeople() {
    const res = await fetch("/api/admin/users", { credentials: "include" });
    if (!res.ok) throw new Error("fail");
    const data = (await res.json()) as { users: AdminUser[] };
    setPeople(data.users);
  }

  async function refreshJobs() {
    const res = await fetch("/api/admin/jobs", { credentials: "include" });
    if (!res.ok) throw new Error("fail");
    const data = (await res.json()) as { jobs: AdminJob[] };
    setJobs(data.jobs);
  }

  async function banUser(userId: string) {
    if (!window.confirm(t.reports.confirmBan)) return;
    setBusyId(userId);
    setActionError(null);
    try {
      const res = await fetch(`/api/admin/users/${userId}/ban`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("fail");
      await refreshPeople();
    } catch {
      setActionError(t.reports.actionFailed);
    } finally {
      setBusyId(null);
    }
  }

  async function unbanUser(userId: string) {
    if (!window.confirm(t.reports.confirmUnban)) return;
    setBusyId(userId);
    setActionError(null);
    try {
      const res = await fetch(`/api/admin/users/${userId}/unban`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("fail");
      await refreshPeople();
    } catch {
      setActionError(t.reports.actionFailed);
    } finally {
      setBusyId(null);
    }
  }

  async function deleteUser(userId: string) {
    if (!window.confirm(t.reports.confirmDeleteUser)) return;
    setBusyId(userId);
    setActionError(null);
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("fail");
      await refreshPeople();
    } catch {
      setActionError(t.reports.actionFailed);
    } finally {
      setBusyId(null);
    }
  }

  async function deleteJob(jobId: string) {
    if (!window.confirm(t.reports.confirmDeleteJob)) return;
    setBusyId(jobId);
    setActionError(null);
    try {
      const res = await fetch(`/api/admin/jobs/${jobId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("fail");
      await refreshJobs();
    } catch {
      setActionError(t.reports.actionFailed);
    } finally {
      setBusyId(null);
    }
  }

  async function openMessage(item: ContactMessage) {
    setSelected(item);
    if (item.read_at) return;
    const res = await fetch(`/api/contact/${item.id}/read`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return;
    const readAt = new Date().toISOString();
    setItems((prev) =>
      prev
        ? prev.map((m) => (m.id === item.id ? { ...m, read_at: readAt } : m))
        : prev,
    );
    setSelected((prev) =>
      prev?.id === item.id ? { ...prev, read_at: readAt } : prev,
    );
    setUnread((n) => Math.max(0, n - 1));
  }

  const maxDay = Math.max(
    1,
    ...(overview?.views_by_day.map((d) => d.count) ?? [1]),
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t.reports.title}</h1>
        <p className="mt-1 text-muted">{t.reports.subtitle}</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-3">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition",
              tab === id
                ? "bg-brand-600 text-white"
                : "text-muted hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-950",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
            {id === "support" && unread > 0 && (
              <Badge tone="brand" className="ms-1">
                {unread}
              </Badge>
            )}
          </button>
        ))}
      </div>

      {loading && <p className="text-muted">{t.reports.loading}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && tab === "overview" && overview && (
        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label={t.reports.statManagers} value={overview.managers} />
            <StatCard
              label={t.reports.statCandidates}
              value={overview.candidates}
            />
            <StatCard
              label={t.reports.statCompanies}
              value={overview.companies}
            />
            <StatCard label={t.reports.statJobs} value={overview.jobs} />
            <StatCard
              label={t.reports.statInterviews}
              value={overview.interviews}
            />
            <StatCard
              label={t.reports.statUnread}
              value={overview.contact_unread}
            />
            <StatCard
              label={t.reports.statViews}
              value={overview.page_views_total}
              hint={`${overview.page_views_7d} ${t.reports.last7d} · ${overview.page_views_30d} ${t.reports.last30d}`}
            />
            <StatCard
              label={t.reports.statVisitors}
              value={overview.unique_visitors}
              hint={`${overview.unique_visitors_30d} ${t.reports.last30d}`}
            />
          </div>

          {Object.keys(overview.interviews_by_status).length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-4">
              <h2 className="text-sm font-semibold">
                {t.reports.interviewStatus}
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(overview.interviews_by_status).map(
                  ([status, count]) => (
                    <Badge key={status} tone="neutral">
                      {status}: {count}
                    </Badge>
                  ),
                )}
              </div>
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Eye className="h-4 w-4 text-brand-600" />
                {t.reports.viewsChart}
              </div>
              <div className="mt-4 flex h-32 items-end gap-1">
                {overview.views_by_day.length === 0 ? (
                  <p className="text-sm text-muted">{t.reports.noViewsYet}</p>
                ) : (
                  overview.views_by_day.map((d) => (
                    <div
                      key={d.day}
                      className="group relative flex-1 rounded-t bg-brand-500/80 min-w-0"
                      style={{ height: `${(d.count / maxDay) * 100}%` }}
                      title={`${d.day}: ${d.count}`}
                    />
                  ))
                )}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4">
              <h2 className="text-sm font-semibold">{t.reports.topPaths}</h2>
              {overview.top_paths.length === 0 ? (
                <p className="mt-3 text-sm text-muted">{t.reports.noViewsYet}</p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {overview.top_paths.map((p) => (
                    <li
                      key={p.path}
                      className="flex items-center justify-between gap-3 py-2 text-sm"
                    >
                      <span className="truncate font-mono text-xs">
                        {p.path}
                      </span>
                      <span className="shrink-0 tabular-nums text-muted">
                        {p.count}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && tab === "activity" && activity && (
        <div className="rounded-2xl border border-border bg-card">
          <ul className="divide-y divide-border">
            {activity.length === 0 ? (
              <li className="p-8 text-center text-sm text-muted">
                {t.reports.activityEmpty}
              </li>
            ) : (
              activity.map((item) => (
                <li
                  key={`${item.kind}-${item.id}-${item.created_at}`}
                  className="flex flex-wrap items-start justify-between gap-2 px-4 py-3"
                >
                  <div>
                    <Badge tone="neutral" className="mb-1">
                      {item.kind}
                    </Badge>
                    <p className="text-sm">{item.summary}</p>
                  </div>
                  <p className="text-xs text-muted">
                    {formatWhen(
                      item.created_at,
                      document.documentElement.lang,
                    )}
                  </p>
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {!loading && !error && tab === "people" && people && (
        <div className="space-y-3">
          {actionError && (
            <p className="text-sm text-red-600">{actionError}</p>
          )}
          <div className="overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full min-w-[800px] text-start text-sm">
              <thead className="border-b border-border bg-surface/60 text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">{t.reports.colName}</th>
                  <th className="px-4 py-3 font-medium">{t.reports.colEmail}</th>
                  <th className="px-4 py-3 font-medium">{t.reports.colCountry}</th>
                  <th className="px-4 py-3 font-medium">{t.reports.colRole}</th>
                  <th className="px-4 py-3 font-medium">{t.reports.colCompany}</th>
                  <th className="px-4 py-3 font-medium">{t.reports.colJoined}</th>
                  <th className="px-4 py-3 font-medium">{t.reports.colActions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {people.map((u) => {
                  const isSelf = u.id === currentUserId;
                  const banned = Boolean(u.banned_at);
                  return (
                    <tr key={u.id}>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{u.full_name || "—"}</span>
                          {banned && (
                            <Badge tone="red">{t.reports.bannedBadge}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">{u.email}</td>
                      <td className="px-4 py-3">{u.country || "—"}</td>
                      <td className="px-4 py-3">{u.role}</td>
                      <td className="px-4 py-3">{u.company_name || "—"}</td>
                      <td className="px-4 py-3 text-muted">
                        {formatWhen(u.created_at, document.documentElement.lang)}
                      </td>
                      <td className="px-4 py-3">
                        {!isSelf && (
                          <div className="flex flex-wrap gap-2">
                            {banned ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                loading={busyId === u.id}
                                onClick={() => void unbanUser(u.id)}
                              >
                                {t.reports.unban}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                loading={busyId === u.id}
                                onClick={() => void banUser(u.id)}
                              >
                                {t.reports.ban}
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              loading={busyId === u.id}
                              onClick={() => void deleteUser(u.id)}
                            >
                              {t.reports.deleteUser}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && tab === "jobs" && jobs && (
        <div className="space-y-3">
          {actionError && (
            <p className="text-sm text-red-600">{actionError}</p>
          )}
          {jobs.length === 0 ? (
            <p className="rounded-2xl border border-border bg-card px-4 py-8 text-center text-sm text-muted">
              {t.reports.jobsEmpty}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-border bg-card">
              <table className="w-full min-w-[720px] text-start text-sm">
                <thead className="border-b border-border bg-surface/60 text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">{t.reports.colTitle}</th>
                    <th className="px-4 py-3 font-medium">{t.reports.colCompany}</th>
                    <th className="px-4 py-3 font-medium">{t.reports.colStatus}</th>
                    <th className="px-4 py-3 font-medium">
                      {t.reports.colVisibility}
                    </th>
                    <th className="px-4 py-3 font-medium">{t.reports.colJoined}</th>
                    <th className="px-4 py-3 font-medium">{t.reports.colActions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {jobs.map((j) => (
                    <tr key={j.id}>
                      <td className="px-4 py-3 font-medium">{j.title}</td>
                      <td className="px-4 py-3">{j.company_name}</td>
                      <td className="px-4 py-3">{j.status}</td>
                      <td className="px-4 py-3">{j.visibility}</td>
                      <td className="px-4 py-3 text-muted">
                        {formatWhen(j.created_at, document.documentElement.lang)}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="danger"
                          loading={busyId === j.id}
                          onClick={() => void deleteJob(j.id)}
                        >
                          {t.reports.deleteJob}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!loading && !error && tab === "companies" && companies && (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full min-w-[560px] text-start text-sm">
            <thead className="border-b border-border bg-surface/60 text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">{t.reports.colCompany}</th>
                <th className="px-4 py-3 font-medium">{t.reports.colType}</th>
                <th className="px-4 py-3 font-medium">{t.reports.colUsers}</th>
                <th className="px-4 py-3 font-medium">{t.reports.colJobs}</th>
                <th className="px-4 py-3 font-medium">{t.reports.colJoined}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {companies.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3">{c.company_type}</td>
                  <td className="px-4 py-3 tabular-nums">{c.user_count}</td>
                  <td className="px-4 py-3 tabular-nums">{c.job_count}</td>
                  <td className="px-4 py-3 text-muted">
                    {formatWhen(c.created_at, document.documentElement.lang)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && tab === "support" && items && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="rounded-2xl border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-2 font-semibold">
                <Inbox className="h-5 w-5 text-brand-600" />
                {t.reports.inbox}
              </div>
              {unread > 0 && (
                <Badge tone="brand">
                  {unread} {t.reports.unread}
                </Badge>
              )}
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted">
                <Mail className="mx-auto h-10 w-10 text-muted/60" />
                <p className="mt-3 font-medium text-foreground">
                  {t.reports.empty}
                </p>
                <p className="mt-1">{t.reports.emptyDesc}</p>
              </div>
            ) : (
              <ul className="max-h-[70vh] divide-y divide-border overflow-y-auto">
                {items.map((item) => {
                  const active = selected?.id === item.id;
                  const unreadMsg = !item.read_at;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => openMessage(item)}
                        className={`w-full px-4 py-3 text-start transition-colors hover:bg-brand-50 dark:hover:bg-brand-950 ${
                          active ? "bg-brand-50 dark:bg-brand-950" : ""
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span
                            className={`text-sm ${
                              unreadMsg ? "font-bold" : "font-medium"
                            }`}
                          >
                            {item.name}
                          </span>
                          {unreadMsg && (
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-600" />
                          )}
                        </div>
                        <p className="mt-0.5 truncate text-sm text-foreground/80">
                          {item.subject}
                        </p>
                        <p className="mt-1 text-xs text-muted">
                          {formatWhen(
                            item.created_at,
                            document.documentElement.lang,
                          )}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            {!selected ? (
              <div className="flex h-full min-h-[240px] flex-col items-center justify-center text-center text-sm text-muted">
                <Mail className="h-10 w-10 text-muted/60" />
                <p className="mt-3">{t.reports.selectHint}</p>
              </div>
            ) : (
              <div>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold">{selected.subject}</h2>
                    <p className="mt-1 text-sm text-muted">
                      {selected.name} ·{" "}
                      <a
                        href={`mailto:${selected.email}`}
                        className="text-brand-700 hover:underline"
                      >
                        {selected.email}
                      </a>
                    </p>
                    <p className="mt-1 text-xs text-muted">
                      {formatWhen(
                        selected.created_at,
                        document.documentElement.lang,
                      )}
                    </p>
                  </div>
                  <a
                    href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 text-sm font-semibold text-foreground hover:bg-surface"
                  >
                    {t.reports.reply}
                  </a>
                </div>
                <div className="mt-6 whitespace-pre-wrap rounded-xl bg-surface p-4 text-sm leading-relaxed text-foreground/90">
                  {selected.message}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
